/**
 * greatCircle (#441 Phase 2) — the geometry the simulated leg flies.
 *
 * A great circle is the shortest path between two points on a sphere. It
 * is a straight line on the globe and only looks curved on a flat map,
 * which is why a simulated leg cannot just interpolate latitude and
 * longitude linearly: on a long leg that path drifts hundreds of miles
 * away from where an aircraft would actually be, and the NOTAM list this
 * feature exists to preview would be wrong.
 *
 * No dependency, and only one copy: `useContextualNotams` (the 500 m move
 * trigger) and `contextualHitToFeature` (the bearing on a hit) both call
 * in here rather than carrying their own. They used to, on two different
 * Earth radii.
 *
 * Every function takes and returns `{ lat, lon }` in DEGREES. Note that
 * the app's `FlightRoutePoint.coords` is GeoJSON `[lng, lat]` — the
 * opposite order — so callers convert at that boundary, not here.
 */

/** Mean Earth radius in metres (WGS-84 authalic sphere). */
const EARTH_RADIUS_M = 6_371_008.8;

export interface LatLon {
  lat: number;
  lon: number;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** True when both coordinates are finite and inside the valid ranges. */
export function isValidLatLon(p: LatLon): boolean {
  return (
    Number.isFinite(p.lat) &&
    Number.isFinite(p.lon) &&
    p.lat >= -90 &&
    p.lat <= 90 &&
    p.lon >= -180 &&
    p.lon <= 180
  );
}

/**
 * Angular separation in radians. Haversine — chosen over the spherical
 * law of cosines because it keeps its precision on short legs, which is
 * most of what this simulator flies.
 */
function angularDistanceRad(from: LatLon, to: LatLon): number {
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Great-circle distance in metres. */
export function distanceM(from: LatLon, to: LatLon): number {
  return EARTH_RADIUS_M * angularDistanceRad(from, to);
}

/**
 * True when the pair has no single shortest path, so no leg can be flown
 * between them: the same point (no direction), or antipodal (infinitely
 * many shortest paths, all equally short). Both make the interpolation
 * divide by an underflowing sin(d).
 */
export function isDegenerateLeg(from: LatLon, to: LatLon): boolean {
  const d = angularDistanceRad(from, to);
  return d < 1e-12 || Math.PI - d < 1e-9;
}

/**
 * Initial bearing in degrees clockwise from true north, 0–360.
 *
 * "Initial" matters: along a great circle the bearing changes
 * continuously, so this is the heading at `from` for the leg to `to`, not
 * a constant course. That is exactly what a heading indicator shows, and
 * it is why the simulator recomputes it every tick from the CURRENT
 * position rather than once for the whole leg.
 */
export function initialBearingDeg(from: LatLon, to: LatLon): number {
  const dLon = toRad(to.lon - from.lon);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * The point a given fraction of the way along the great circle from
 * `from` to `to`. `fraction` is clamped to [0, 1], so 0 returns the
 * departure and 1 the destination.
 */
export function interpolate(from: LatLon, to: LatLon, fraction: number): LatLon {
  const f = Math.min(1, Math.max(0, fraction));
  const d = angularDistanceRad(from, to);
  // Both poles of the formula below divide by sin(d). Coincident
  // endpoints (d → 0) have an undefined direction; ANTIPODAL endpoints
  // (d → π) have infinitely many shortest paths and sin(d) underflows
  // from the other side, which would return floating-point noise as a
  // position. Short-circuit both rather than divide.
  if (d < 1e-12) return { lat: from.lat, lon: from.lon };
  if (Math.PI - d < 1e-9) return { lat: from.lat, lon: from.lon };

  const sinD = Math.sin(d);
  const a = Math.sin((1 - f) * d) / sinD;
  const b = Math.sin(f * d) / sinD;

  const lat1 = toRad(from.lat);
  const lon1 = toRad(from.lon);
  const lat2 = toRad(to.lat);
  const lon2 = toRad(to.lon);

  const x = a * Math.cos(lat1) * Math.cos(lon1) + b * Math.cos(lat2) * Math.cos(lon2);
  const y = a * Math.cos(lat1) * Math.sin(lon1) + b * Math.cos(lat2) * Math.sin(lon2);
  const z = a * Math.sin(lat1) + b * Math.sin(lat2);

  return {
    lat: toDeg(Math.atan2(z, Math.hypot(x, y))),
    lon: toDeg(Math.atan2(y, x))
  };
}
